/**
 * Gallery UI Store - User interface state and interactions
 */

import { ErrorService } from '../services/error-service.js';

export function createGalleryUIStore() {
  return {
    config_modal_open: false,
    variant_modal_open: false,
    filter_modal_open: false,
    variant_selection_in_progress: false,
    interactive_panel_locked: false,

    randomizing: false,
    loading: false,
    loading_fadeout: false,
    transitioning_layout: false,
    cart_loading: false,

    active_artwork_index: null,
    hover_artwork_index: null,
    last_active_artwork_index_before_modal: null,
    last_active_artwork_index_before_preview: null,
    last_active_position_before_orientation_change: null,
    user_has_interacted: false,

    retry_count: 0,

    save_success: false,
    save_message: null,

    cart_success_message: null,
    cart_success_timeout: null,

    loading_progress: 0,
    target_progress: 0,
    current_loading_text: 'Loading catalogue...',
    text_is_changing: false,
    current_message_index: 0,
    loading_messages: [],
    loading_text_interval: null,
    progress_animation_interval: null,
    text_cycle_duration: 3000,

    preview_button_disabled: false,
    exit_preview_visible: false,
    preview_cursor_mode: 'default',

    backgroundLoading: false,
    totalProductCount: 0,
    loadedProductCount: 0,

    searching: false,
    search_focused: false,
    active_search_query: '',
    search_result_count: 0,

    get loadingProgress() {
      if (this.totalProductCount === 0) return 0;
      return Math.round((this.loadedProductCount / this.totalProductCount) * 100);
    },

    get isFullyLoaded() {
      return this.loadedProductCount === this.totalProductCount && this.totalProductCount > 0;
    },

    get search_status_message() {
      if (this.searching) {
        return 'Searching...';
      }

      if (this.active_search_query) {
        const countText = this.search_result_count === 1 ? 'result' : 'results';
        return `${this.search_result_count} ${countText} for "${this.active_search_query}"`;
      }

      return '';
    },

    async setActiveArtwork(index) {
      // Don't allow closing if panel is locked (filter operations in progress)
      if (index === null && this.interactive_panel_locked) {
        return;
      }

      // If variant modal is open, close it first before opening carousel
      if (this.variant_modal_open) {
        this.closeVariantModal();
        // Wait for modal slide-down transition to complete
        await new Promise(resolve => setTimeout(resolve, 350));
      }

      this.active_artwork_index = index;

      if (index !== null) {
        this.user_has_interacted = true;
      }
    },

    setHoverArtwork(index) {
      this.hover_artwork_index = index;
    },

    clearHover() {
      this.hover_artwork_index = null;
    },

    setError(error, context = 'UI') {
      if (error) {
        const formatted = ErrorService.formatError(error, context);
        ErrorService.logError(formatted, context);
      }
    },

    incrementRetry() {
      this.retry_count++;
    },

    resetRetry() {
      this.retry_count = 0;
    },

    setLoading(loading, message = null) {
      this.loading = loading;
      if (message) {
        this.current_loading_text = message;
      }
    },

    setLoadingFadeout(fadeout) {
      this.loading_fadeout = fadeout;
    },

    setCartLoading(loading) {
      this.cart_loading = loading;
    },

    /**
     * @param {string} message - The success message to display
     * @param {number} duration - How long to show the message in milliseconds
     */
    showSuccessMessage(message, duration = 3000) {
      if (this.cart_success_timeout) {
        clearTimeout(this.cart_success_timeout);
      }

      this.cart_success_message = message;

      this.cart_success_timeout = setTimeout(() => {
        this.cart_success_message = null;
        this.cart_success_timeout = null;
      }, duration);
    },

    clearSuccessMessage() {
      if (this.cart_success_timeout) {
        clearTimeout(this.cart_success_timeout);
        this.cart_success_timeout = null;
      }
      this.cart_success_message = null;
    },

    updateLoadingProgress(target) {
      this.target_progress = target;
    },

    setTransitioningLayout(transitioning) {
      this.transitioning_layout = transitioning;
    },

    openConfigModal() {
      this.config_modal_open = true;
    },

    closeConfigModal() {
      this.config_modal_open = false;
    },

    async openVariantModal() {
      if (this.active_artwork_index !== null) {
        // Save the last active artwork index before closing carousel
        this.last_active_artwork_index_before_modal = this.active_artwork_index;
        this.active_artwork_index = null;
        await new Promise(resolve => setTimeout(resolve, 350));
      }

      this.variant_modal_open = true;
      document.body.style.overflow = 'hidden';
    },

    closeVariantModal() {
      this.variant_modal_open = false;
      document.body.style.overflow = '';
    },

    lockPanel() {
      this.interactive_panel_locked = true;
    },

    unlockPanel() {
      this.interactive_panel_locked = false;
    },

    // Loading screen methods
    initializeLoadingMessages(messages_element) {
      if (messages_element) {
        try {
          this.loading_messages = JSON.parse(messages_element.textContent);
        } catch (error) {
          this.loading_messages = [];
        }
      } else {
        this.loading_messages = [];
      }

      this.current_loading_text = this.loading_messages[0] || 'Loading catalogue...';
      this.current_message_index = 0;
    },

    startLoadingScreen() {
      this.loading = true;
      this.loading_progress = 0;
      this.target_progress = 0;
      this.startTextCycling();
      this.startProgressAnimation();
    },

    async stopLoadingScreen() {
      if (this.target_progress < 100) {
        this.target_progress = 100;
      }

      await this.waitForProgressCompletion();

      [this.loading_text_interval, this.progress_animation_interval].forEach(interval => {
        if (interval) clearInterval(interval);
      });
      this.loading_text_interval = null;
      this.progress_animation_interval = null;

      this.loading_fadeout = true;
      this.loading = false;

      await new Promise(resolve => setTimeout(resolve, 700));

      Object.assign(this, {
        loading_progress: 0,
        target_progress: 0,
        text_is_changing: false,
        current_message_index: 0
      });
      this.loading_fadeout = false;
    },

    startProgressAnimation() {
      if (this.progress_animation_interval) {
        clearInterval(this.progress_animation_interval);
      }

      const TICK_INTERVAL = 160;
      const MIN_STEP = 1.5;
      const MAX_STEP = 6.5;
      const IDLE_STEP = 0.6;

      this.progress_animation_interval = setInterval(() => {
        const current = this.loading_progress;
        const target = this.target_progress;

        if (current < target) {
          const delta = target - current;
          let eased_step = Math.min(Math.max(delta * 0.3, MIN_STEP), MAX_STEP);

          if (target >= 100) {
            eased_step = Math.max(eased_step, 2.5);
          }
          this.loading_progress = Math.min(current + eased_step, target);
        } else if (current < 99.5) {
          this.loading_progress = Math.min(current + IDLE_STEP, 99.5);
        }

        if (this.loading_progress >= 100) {
          this.loading_progress = 100;
          clearInterval(this.progress_animation_interval);
          this.progress_animation_interval = null;
        }
      }, TICK_INTERVAL);
    },

    async waitForProgressCompletion() {
      const getNow = () => (typeof window !== 'undefined' && window.performance?.now)
        ? window.performance.now()
        : Date.now();

      const startedAt = getNow();
      const MAX_WAIT_MS = 2000;

      while (this.loading_progress < 100) {
        const now = getNow();

        if (now - startedAt >= MAX_WAIT_MS) {
          this.loading_progress = 100;
          break;
        }

        await new Promise(resolve => setTimeout(resolve, 50));
      }

      if (this.loading_progress < 100) {
        this.loading_progress = 100;
      }
    },

    startTextCycling() {
      if (this.loading_text_interval) {
        clearInterval(this.loading_text_interval);
      }

      if (this.loading_messages.length <= 1) return;

      this.loading_text_interval = setInterval(() => {
        this.advanceLoadingText();
      }, this.text_cycle_duration);
    },

    async advanceLoadingText() {
      this.text_is_changing = true;
      await new Promise(resolve => setTimeout(resolve, 300));

      this.current_message_index = (this.current_message_index + 1) % this.loading_messages.length;
      this.current_loading_text = this.loading_messages[this.current_message_index];

      this.text_is_changing = false;
    }
  };
}
